import "./viewUser.css";
import AdminSchoolEmail from "./AdminSchoolEmail";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import useAuth from "../../../../hooks/useAuth";
import axiosInstance from "../../../../api/axios";
import ApplicationDetails from "./applicationDetails";
import ApplicationNavigation from "./applicationNavigation";
import { formatApplicationDate } from "../applicationDate";
import { useUnsavedChanges } from "../../../../context/NavigationGuard";

const DEFAULT_LIST = { page: 1, filters: {}, selectedOption: "first" };

const ViewUserDetails = ({ email }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const headingRef = useRef(null);
  const list = location.state?.applicationList || DEFAULT_LIST;
  const savedQueue = location.state?.applicationQueue;
  const [queue, setQueue] = useState(savedQueue || []);
  const [navigationLoading, setNavigationLoading] = useState(!savedQueue);
  const [navigationError, setNavigationError] = useState("");
  const [navigationRetry, setNavigationRetry] = useState(0);
  const { authState } = useAuth();
  const [application, setApplication] = useState({});
  const [acceptOrDecline, setAD] = useState("");
  const [priorStatus, setPriorStatus] = useState("");
  const [travelReimbursement, setTravelReimbursement] = useState(false);
  const [housingAccommodations, setHousingAccommodations] = useState(false);
  const [oldTravelReimbursement, setOldTravelReimbursement] = useState(false);
  const [oldHousingAccommodations, setOldHousingAccommodations] =
    useState(false);
  const [teammates, setTeammates] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [teamcodeInput, setTeamcodeInput] = useState("");
  const [teamcodeMessage, setTeamcodeMessage] = useState("");
  const [teamcodeError, setTeamcodeError] = useState("");
  const [isSavingTeamcode, setIsSavingTeamcode] = useState(false);
  const [isSavingDecision, setIsSavingDecision] = useState(false);
  const [isSavingVerification, setIsSavingVerification] = useState(false);
  const [hasVerificationChanges, setHasVerificationChanges] = useState(false);
  const [decisionMessage, setDecisionMessage] = useState("");
  const [decisionError, setDecisionError] = useState("");
  const [detailsError, setDetailsError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const isSaving = isSavingDecision || isSavingTeamcode || isSavingVerification;
  const hasChanges = hasVerificationChanges || (!detailsLoading && !!application.email && (
    acceptOrDecline !== priorStatus ||
    travelReimbursement !== oldTravelReimbursement ||
    housingAccommodations !== oldHousingAccommodations ||
    teamcodeInput.trim() !== String(application.teamcode ?? "")
  ));
  useUnsavedChanges(hasChanges, isSaving);

  useEffect(() => {
    if (!authState.isAuthenticated || savedQueue) return;
    let active = true;
    setNavigationLoading(true);
    setNavigationError("");
    axiosInstance.get("/api/applications/navigation", {
      params: { filters: JSON.stringify(list.filters) },
    }).then(({ data }) => {
      if (active) setQueue(data.emails);
    }).catch(() => {
      if (active) setNavigationError("Unable to load application navigation.");
    }).finally(() => {
      if (active) setNavigationLoading(false);
    });
    return () => { active = false; };
  }, [authState.isAuthenticated, list.filters, savedQueue, navigationRetry]);

  const navigateToApplication = (nextEmail) => {
    if (nextEmail && !isSaving) {
      navigate(`/admin/users/${encodeURIComponent(nextEmail)}`, {
        state: { applicationList: list, applicationQueue: queue },
      });
    }
  };

  const index = queue.indexOf(email);
  const navigationProps = {
    index, total: queue.length, loading: navigationLoading, error: navigationError,
    busy: isSaving, filtered: Object.keys(list.filters).length > 0,
    onPrevious: () => navigateToApplication(queue[index - 1]),
    onNext: () => navigateToApplication(queue[index + 1]),
    onBack: () => {
      if (!isSaving) navigate("/admin/users", { state: { applicationList: list } });
    },
    onRetry: () => setNavigationRetry((value) => value + 1),
  };

  const loadTeammates = useCallback(async (teamcode) => {
    if (teamcode === undefined || teamcode === null || teamcode === "") {
      setTeammates([]);
      return;
    }

    try {
      const teammatesResponse = await axiosInstance.get(
        `/api/applications/teammates/${teamcode}`
      );
      setTeammates(teammatesResponse.data.emails || []);
    } catch (err) {
      setTeammates([]);
    }
  }, []);

  const fetchApplication = useCallback(async () => {
    setDetailsLoading(true);
    setDetailsError("");
    try {
      const [applicationResponse, questionsResponse, statusResponse] = await Promise.all([
        axiosInstance.get(`/api/applications/${email}`),
        axiosInstance.get("/api/applications/questions"),
        axiosInstance.get(`/api/applications/status/${email}`),
      ]);

      const nextApplication = applicationResponse.data || {};
      setApplication(nextApplication);
      setQuestions(questionsResponse.data || []);
      setAD(statusResponse.data.status);
      setPriorStatus(statusResponse.data.status);
      setTravelReimbursement(!!statusResponse.data.travel_reimbursement);
      setOldTravelReimbursement(!!statusResponse.data.travel_reimbursement);
      setHousingAccommodations(!!statusResponse.data.housing_accommodations);
      setOldHousingAccommodations(!!statusResponse.data.housing_accommodations);
      setTeamcodeInput(
        nextApplication.teamcode !== undefined &&
          nextApplication.teamcode !== null
          ? String(nextApplication.teamcode)
          : ""
      );

      await loadTeammates(nextApplication.teamcode);
    } catch (err) {
      setApplication({});
      setQuestions([]);
      setTeammates([]);
      setDetailsError("Unable to load this application. Please try again.");
    } finally {
      setDetailsLoading(false);
    }
  }, [email, loadTeammates]);

  const handleDownload = async (e) => {
    e.preventDefault();
    setDownloadError("");
    try {
      const response = await axiosInstance.get(
        `/api/resume/download/${email}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${email}_resume.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError("Unable to download the resume. Please try again.");
    }
  };

  useEffect(() => {
    if (authState.isAuthenticated) {
      fetchApplication();
    }
  }, [authState.isAuthenticated, fetchApplication]);

  useEffect(() => {
    window.scrollTo(0, 0);
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  const handleSelectChange = (event) => {
    setAD(event.target.value);
    setDecisionMessage("");
    setDecisionError("");
  };

  const handleSchoolEmailVerified = useCallback(schoolEmail => {
    setApplication(previous => ({ ...previous, school_email: schoolEmail }));
  }, []);

  const handleSubmitStatus = async (event) => {
    event.preventDefault();
    setIsSavingDecision(true);
    setDecisionMessage("");
    setDecisionError("");
    try {
      await axiosInstance.post(
        `/api/applications/status/${email}`,
        {
          status: acceptOrDecline,
          expected_status: priorStatus,
          travel_reimbursement: travelReimbursement,
          housing_accommodations: housingAccommodations,
        }
      );
      setPriorStatus(acceptOrDecline);
      setOldTravelReimbursement(travelReimbursement);
      setOldHousingAccommodations(housingAccommodations);
      setDecisionMessage("Decision saved.");
    } catch (err) {
      setDecisionError(err.response?.data?.message || "Unable to save the decision. Please try again.");
    } finally {
      setIsSavingDecision(false);
    }
  };

  const handleTeamcodeSubmit = async (event) => {
    event.preventDefault();
    setTeamcodeMessage("");
    setTeamcodeError("");
    setIsSavingTeamcode(true);

    const normalizedTeamcode = teamcodeInput.trim();

    try {
      const response = await axiosInstance.post(
        `/api/applications/teamcode/${email}`,
        { teamcode: normalizedTeamcode }
      );
      const nextTeamcode = response.data.teamcode;
      setApplication((prev) => ({ ...prev, teamcode: nextTeamcode }));
      setTeamcodeInput(nextTeamcode ? String(nextTeamcode) : "");
      setTeamcodeMessage(response.data.message);
      await loadTeammates(nextTeamcode);
    } catch (err) {
      setTeamcodeError(
        err.response?.data?.message || "Unable to update the team code."
      );
    } finally {
      setIsSavingTeamcode(false);
    }
  };

  return (
    <div className="view-user-page">
      <div className="dashboard">
        <div className="vu-shell">
          <ApplicationNavigation {...navigationProps} position="top" />
          <section className="vu-card">
            <div className="vu-header-row">
              <h1 className="vu-page-title" ref={headingRef} tabIndex={-1}>
                {application.first ? `${application.first} ${application.last || ""}`.trim() : "Application"}
              </h1>
              <a className="vu-section-link" href="#application-decision">Jump to status & verification ↓</a>
            </div>
            {detailsError && <p role="alert" className="vu-inline-error">{detailsError} <button type="button" onClick={fetchApplication}>Retry</button></p>}
            <div className="vu-overview-grid">
              <div className="vu-overview-item">
                <p className="vu-item-label">Email</p>
                <p className="vu-item-value">{email}</p>
              </div>
              <div className="vu-overview-item">
                <p className="vu-item-label">Current Status</p>
                <p className="vu-item-value">{priorStatus}</p>
              </div>
              <div className="vu-overview-item">
                <p className="vu-item-label">Travel Reimbursement</p>
                <p className="vu-item-value">
                  {oldTravelReimbursement ? "Yes" : "No"}
                </p>
              </div>
              <div className="vu-overview-item">
                <p className="vu-item-label">Housing Accommodations</p>
                <p className="vu-item-value">
                  {oldHousingAccommodations ? "Yes" : "No"}
                </p>
              </div>
              <div className="vu-overview-item">
                <p className="vu-item-label">Submitted</p>
                <p className="vu-item-value">{formatApplicationDate(application.timestamp)}</p>
              </div>
              <div className="vu-overview-item">
                <p className="vu-item-label">University</p>
                <p className="vu-item-value">{application.school || "Not provided"}</p>
              </div>
            </div>
          </section>

          <section className="vu-card vu-application-card">
            <h2 className="vu-section-title">Application Details</h2>
            <div className="vu-application-wrap">
              <ApplicationDetails
                application={application}
                questions={questions}
                loading={detailsLoading}
              />
            </div>
          </section>

          <section className="vu-card" id="application-decision" tabIndex={-1}>
            <h2 className="vu-section-title">Status & Verification</h2>
            {hasChanges && <p className="vu-unsaved" role="status">Unsaved changes</p>}
            <div className="vu-tools-grid">
              <form onSubmit={handleSubmitStatus} className="vu-form-card">
                <fieldset className="vu-decision-fields" disabled={isSaving || detailsLoading || !application.email}>
                <div className="vu-field">
                  <label className="label-vu" htmlFor="application-status">Decision</label>
                  <select
                    id="application-status"
                    className="confirm-deadline"
                    name="ad"
                    value={acceptOrDecline || "submitted"}
                    onChange={handleSelectChange}
                  >
                    <option value="submitted">No Decision</option>
                    <option value="accepted">Accepted</option>
                    <option value="waitlisted">Waitlisted</option>
                    <option value="rejected">Rejected</option>
                    <option value="waitlist_accepted">Waitlist Accepted</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="waitlist_confirmed">Waitlist Spot Reserved</option>
                    <option value="declined">Declined</option>
                    {priorStatus && !["submitted", "accepted", "waitlisted", "rejected", "waitlist_accepted", "confirmed", "waitlist_confirmed", "declined"].includes(priorStatus) && (
                      <option value={priorStatus} disabled>{priorStatus.replaceAll("_", " ")}</option>
                    )}
                  </select>
                  <p className="vu-helper-text">Confirmed records attendance, including after the deadline. Verify the school email first. Confirmation and decline updates do not send a decision email.</p>
                </div>
                <div className="vu-field">
                  <label className="label-vu" htmlFor="travel-reimbursement">Travel Reimbursement</label>
                  <select
                    id="travel-reimbursement"
                    className="confirm-deadline"
                    value={String(travelReimbursement)}
                    onChange={(e) =>
                      setTravelReimbursement(e.target.value === "true")
                    }
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
                <div className="vu-field">
                  <label className="label-vu" htmlFor="housing-accommodations">Housing Accommodations</label>
                  <select
                    id="housing-accommodations"
                    className="confirm-deadline"
                    value={String(housingAccommodations)}
                    onChange={(e) =>
                      setHousingAccommodations(e.target.value === "true")
                    }
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
                <button type="submit" className="user-dash-b">
                  {isSavingDecision ? "Saving..." : "Save Decision"}
                </button>
                </fieldset>
                {decisionMessage && <p role="status" className="vu-inline-message">{decisionMessage}</p>}
                {decisionError && <p role="alert" className="vu-inline-error">{decisionError}</p>}
              </form>

              <AdminSchoolEmail key={email} email={email}
                disabled={isSaving || detailsLoading || !application.email}
                onBusyChange={setIsSavingVerification} onDirtyChange={setHasVerificationChanges}
                onVerified={handleSchoolEmailVerified} />

              <div className="vu-form-card vu-resume-card">
                <p className="label-vu">Applicant Resume</p>
                <p className="vu-helper-text">
                  Download the applicant&apos;s uploaded PDF resume.
                </p>
                <button
                  onClick={handleDownload}
                  className="button-vu"
                  type="button"
                >
                  Download Resume
                </button>
                {downloadError && <p role="alert" className="vu-inline-error">{downloadError}</p>}
              </div>
            </div>
          </section>

          <section className="vu-card">
            <h2 className="vu-section-title">Team Information</h2>
            <div className="vu-team-grid">
              <div className="vu-team-block">
                <p className="vu-item-label">Team Code</p>
                <p className="vu-team-code">
                  {application.teamcode || "Not provided"}
                </p>
                <form className="vu-team-editor" onSubmit={handleTeamcodeSubmit}>
                  <label className="label-vu" htmlFor="teamcode-input">
                    Update Team Code
                  </label>
                  <input
                    id="teamcode-input"
                    className="confirm-deadline vu-team-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Leave blank to clear"
                    value={teamcodeInput}
                    disabled={isSaving || detailsLoading || !application.email}
                    onChange={(event) => {
                      setTeamcodeInput(
                        event.target.value.replace(/[^\d]/g, "")
                      );
                      setTeamcodeMessage("");
                      setTeamcodeError("");
                    }}
                  />
                  <p className="vu-helper-text">
                    Enter a 6 to 8 digit team code, or leave this blank to clear
                    it.
                  </p>
                  {teamcodeMessage && (
                    <p role="status" className="vu-inline-message">{teamcodeMessage}</p>
                  )}
                  {teamcodeError && (
                    <p role="alert" className="vu-inline-error">{teamcodeError}</p>
                  )}
                  <button
                    type="submit"
                    className="user-dash-b"
                    disabled={isSaving || detailsLoading || !application.email}
                  >
                    {isSavingTeamcode ? "Saving..." : "Save Team Code"}
                  </button>
                </form>
              </div>
              <div className="vu-team-block">
                <p className="vu-item-label">Team Members</p>
                {teammates.length === 0 ? (
                  <p className="vu-helper-text">No teammates listed yet.</p>
                ) : (
                  <ul className="ad-vu-list">
                    {teammates.map((email, index) => (
                      <li key={index} className="ad-vu-score">
                        <Link className="ad-vu-score-a" to={`/admin/users/${encodeURIComponent(email)}`}
                          state={{ applicationList: list, applicationQueue: queue }}>
                          {email}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
          <ApplicationNavigation {...navigationProps} position="bottom" />
        </div>
      </div>
    </div>
  );
};

const ViewUser = () => {
  const { email } = useParams();
  return <ViewUserDetails key={email} email={email} />;
};

export default ViewUser;
